// Module-scoped concurrency limiter for snapshot polling across the
// camera wall. The wall renders one <CameraTile> per camera and each
// tile schedules its own jittered poll, so without a global cap we
// could see N independent fetches firing simultaneously when their
// timers happen to align.
//
// LLD §8.3 / 09 §8.2 row 7: AVA's snapshot endpoint sits inside the
// global 100 TPS budget. A 16-tile wall hammering AVA in lockstep on
// every poll cycle isn't dangerous (it's nowhere near 100 TPS) but the
// browser-side parallelism is what we care about — keeping concurrent
// fetches small reduces CPU spikes (decoding + GC) and keeps each tile
// responsive when one upstream is slow.
//
// Cap = 6: matches a typical browser per-host HTTP/1.1 connection
// cap (Chrome/Firefox/Safari all use 6). The BFF returns JSON only —
// JPEGs flow browser↔CDN over a different host — so the cap shapes
// concurrent BFF probes (cheap) + concurrent <img> decodes. 6 keeps the
// above-the-fold grid hydrated quickly without saturating CPU on
// JPEG decode under load.
//
// Two flavors:
//   - createSemaphore(max): plain FIFO permit pool.
//   - createPrioritySemaphore(max, opts): three-band priority pool that
//     gates lower bands behind an "in-flight high" budget so visible
//     work always wins on contention. Used by the camera wall so the
//     above-the-fold tiles populate before below-the-fold tiles even
//     start their network fetch.

/** Default cap for concurrent snapshot fetches across the whole app. */
export const DEFAULT_SNAPSHOT_CONCURRENCY = 6;

/** Priority bands. Lower number = higher priority. */
export type Priority = "high" | "normal" | "low";

const PRIORITY_RANK: Record<Priority, number> = {
  high: 0,
  normal: 1,
  low: 2,
};

export interface Semaphore {
  /**
   * Acquire a permit. Resolves once a slot is free. The caller MUST call
   * the returned `release` exactly once when their work is done.
   *
   * @param priority Optional priority band. Higher-priority waiters are
   *   served before lower-priority ones; "low" waiters yield while any
   *   "high" work is still in flight (priority semaphores only).
   */
  acquire(priority?: Priority): Promise<() => void>;

  /**
   * Try to acquire a permit synchronously. Returns the release function
   * when a slot was immediately available, or `null` when the semaphore
   * is fully booked. Used by callers that prefer to drop work over
   * queueing (e.g. snapshot polls — the next interval will retry).
   */
  tryAcquire(priority?: Priority): (() => void) | null;

  /** Current number of in-flight permits. Exposed for tests. */
  inFlight(): number;

  /** Pending acquirers waiting on a permit. Exposed for tests. */
  waiting(): number;
}

export function createSemaphore(max: number): Semaphore {
  if (max <= 0 || !Number.isInteger(max)) {
    throw new Error(`Semaphore max must be a positive integer, got ${max}`);
  }

  let active = 0;
  const queue: Array<(release: () => void) => void> = [];

  function release(): void {
    active -= 1;
    const next = queue.shift();
    if (next) {
      active += 1;
      // Defer resolution so the releaser's microtask completes before
      // the next worker starts — keeps stack traces shallow under load.
      queueMicrotask(() => next(release));
    }
  }

  return {
    acquire(): Promise<() => void> {
      if (active < max) {
        active += 1;
        return Promise.resolve(release);
      }
      return new Promise((resolve) => {
        queue.push(resolve);
      });
    },
    tryAcquire(): (() => void) | null {
      if (active < max) {
        active += 1;
        return release;
      }
      return null;
    },
    inFlight(): number {
      return active;
    },
    waiting(): number {
      return queue.length;
    },
  };
}

export interface PrioritySemaphoreOptions {
  /**
   * Reserved slots that only "high" callers may occupy. Setting this
   * below `max` lets non-high waiters fill the remainder; setting it
   * to `max` makes high work strictly preempt all others.
   *
   * Default: ceil(max / 2) — half the pool is always available for
   * visible work, the other half is shared.
   */
  highReservedSlots?: number;
  /**
   * If true, lower-priority waiters yield while any high-priority slot
   * is in use, even when slots are free. Default false: low-priority
   * work fills idle capacity but high-priority work still skips the
   * queue.
   */
  starveLowWhenHighActive?: boolean;
}

interface Waiter {
  rank: number;
  /** Tie-break so equal-priority waiters serve FIFO. */
  seq: number;
  resolve: (release: () => void) => void;
}

/**
 * Three-band priority semaphore. "high" waiters jump the queue and
 * have a reserved slot budget; "low" waiters yield to any "high"
 * in-flight when `starveLowWhenHighActive` is set. "normal" sits in
 * the middle.
 */
export function createPrioritySemaphore(
  max: number,
  opts: PrioritySemaphoreOptions = {},
): Semaphore {
  if (max <= 0 || !Number.isInteger(max)) {
    throw new Error(`Semaphore max must be a positive integer, got ${max}`);
  }
  const highReserved = Math.max(
    0,
    Math.min(max, opts.highReservedSlots ?? Math.ceil(max / 2)),
  );
  const starveLow = opts.starveLowWhenHighActive === true;

  let activeTotal = 0;
  let activeHigh = 0;
  let nextSeq = 0;
  // Min-heap-style sorted insert; queue is small (bounded by tile
  // count), so an O(n) insert is cheaper than pulling in a heap dep.
  const queue: Waiter[] = [];

  function canRunNow(priority: Priority): boolean {
    if (activeTotal >= max) return false;
    if (priority !== "high" && activeTotal >= max - highReserved) {
      // The reserved tail is for "high" only.
      return false;
    }
    if (priority === "low" && starveLow && activeHigh > 0) {
      return false;
    }
    return true;
  }

  function release(priority: Priority): void {
    activeTotal -= 1;
    if (priority === "high") activeHigh -= 1;
    // Walk the queue and serve any waiter whose priority can now run.
    // We scan in rank order so a freed slot wakes the highest-priority
    // waiter first.
    for (let i = 0; i < queue.length; i++) {
      const w = queue[i];
      if (w === undefined) continue;
      const wPriority = rankToPriority(w.rank);
      if (canRunNow(wPriority)) {
        queue.splice(i, 1);
        activeTotal += 1;
        if (wPriority === "high") activeHigh += 1;
        const r = (): void => release(wPriority);
        queueMicrotask(() => w.resolve(r));
        return;
      }
    }
  }

  function rankToPriority(rank: number): Priority {
    if (rank === PRIORITY_RANK.high) return "high";
    if (rank === PRIORITY_RANK.normal) return "normal";
    return "low";
  }

  function enqueue(priority: Priority): Promise<() => void> {
    const rank = PRIORITY_RANK[priority];
    return new Promise((resolve) => {
      const waiter: Waiter = { rank, seq: nextSeq++, resolve };
      // Sorted insert: find the first waiter we outrank (lower rank
      // wins; equal rank → FIFO via seq).
      let i = 0;
      while (i < queue.length) {
        const cur = queue[i];
        if (cur === undefined) break;
        if (
          cur.rank < waiter.rank ||
          (cur.rank === waiter.rank && cur.seq < waiter.seq)
        ) {
          i++;
          continue;
        }
        break;
      }
      queue.splice(i, 0, waiter);
    });
  }

  return {
    acquire(priority: Priority = "normal"): Promise<() => void> {
      if (canRunNow(priority)) {
        activeTotal += 1;
        if (priority === "high") activeHigh += 1;
        const r = (): void => release(priority);
        return Promise.resolve(r);
      }
      return enqueue(priority);
    },
    tryAcquire(priority: Priority = "normal"): (() => void) | null {
      if (canRunNow(priority)) {
        activeTotal += 1;
        if (priority === "high") activeHigh += 1;
        return (): void => release(priority);
      }
      return null;
    },
    inFlight(): number {
      return activeTotal;
    },
    waiting(): number {
      return queue.length;
    },
  };
}

// Module-singleton used by useSnapshotPoll. Priority-aware so visible
// tiles win the fetch race over near/far tiles. Exposed for tests.
export const snapshotSemaphore: Semaphore = createPrioritySemaphore(
  DEFAULT_SNAPSHOT_CONCURRENCY,
);
